import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Handshake, Rocket, MessageSquare, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS = [
  { value: "beta", label: "Probar la beta", icon: Rocket },
  { value: "colaboracion", label: "Colaborar", icon: Handshake },
  { value: "general", label: "Consulta general", icon: MessageSquare },
];

export default function ContactSection() {
  const [tipo, setTipo] = useState("beta");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Completa todos los campos antes de enviar.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, tipo, message }),
      });
      if (!res.ok) throw new Error("fail");
      toast.success("¡Mensaje enviado! Te responderemos pronto.");
      setName("");
      setEmail("");
      setMessage("");
      setTipo("beta");
    } catch {
      toast.error("No pudimos enviar tu mensaje. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/10 bg-[#0F172A] px-4 py-3.5 font-body text-sm text-white placeholder:text-[#64748B] transition-colors duration-200 focus:border-[#FF5500] focus:outline-none focus:ring-1 focus:ring-[#FF5500]";

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative scroll-mt-24 border-t border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 md:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="flex flex-col gap-6"
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#FF5500]" />
            <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF5500]">
              Contacto
            </span>
          </div>
          <h2 className="font-heading text-3xl font-black leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">
            ¿Quieres subir al escenario con nosotros?
          </h2>
          <p className="max-w-md font-body text-base leading-relaxed text-[#94A3B8]">
            Ya sea para probar la versión beta, organizar tus eventos o buscar
            una colaboración, escríbenos. Somos un equipo apasionado por el
            freestyle y la cultura del rap.
          </p>
          <a
            href="https://instagram.com/lapinza.app"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram"
            className="mt-2 w-fit font-ui text-sm font-bold text-white transition-colors duration-200 hover:text-[#FF5500]"
          >
            @lapinza.app <span className="text-[#FF5500]">en Instagram →</span>
          </a>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="flex flex-col gap-5"
        >
          <div className="flex flex-wrap gap-3">
            {TIPOS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTipo(t.value)}
                data-testid={`contact-tipo-${t.value}`}
                className={`inline-flex items-center gap-2 border px-4 py-2.5 font-ui text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
                  tipo === t.value
                    ? "border-[#FF5500] bg-[#FF5500] text-white"
                    : "border-white/10 bg-[#0F172A] text-[#94A3B8] hover:border-[#FF5500] hover:text-white"
                }`}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre o el de tu organización"
            data-testid="contact-name"
            className={inputCls}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Tu correo electrónico"
            data-testid="contact-email"
            className={inputCls}
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Cuéntanos qué tienes en mente…"
            rows={5}
            data-testid="contact-message"
            className={`${inputCls} resize-none`}
          />

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="group inline-flex items-center justify-center gap-2 bg-[#FF5500] px-7 py-4 font-ui text-xs font-bold uppercase tracking-[0.2em] text-white transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_14px_40px_-10px_rgba(255,85,0,0.7)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                Enviar mensaje
                <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
